import { isApiError } from "@pokifolio/api-client";
import {
  ApproveResearchRequestSchema,
  CloseResearchRequestSchema,
  type MeResponse,
  PRICE_FRESH_HOURS,
  RejectResearchRequestSchema,
  type ResearchRecord,
  SubmitResearchRequestSchema,
  isPriceFresh,
} from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { type SubmitEvent, useId, useState } from "react";

import { Alert, ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Field } from "../../components/ui/field";
import { Input, Textarea } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { useCurrentUser } from "../auth/session";
import { formatDateTime, formatMoney } from "../search/format";
import { type StepCall, useResearchStep } from "./research-queries";

type Step = ReturnType<typeof useResearchStep>;

const usd = (amount: string) => formatMoney({ amount, currency: "USD" });

/** Who the signed-in person is for this record. Mirrors the server's rules; the server always decides. */
function rolesFor(research: ResearchRecord, me: MeResponse) {
  const isOwner = research.owner.id === me.user.id;
  return {
    isOwner,
    mayChange:
      me.permissions.includes("research.update_any") ||
      (isOwner && me.permissions.includes("research.update_own")),
    mayDecide: me.permissions.includes("research.approve"),
  };
}

/**
 * Asking the admin before buying (D-47): send for approval, withdraw, approve (the maximum may be lowered),
 * reject with a reason, revise and send again (D-49), or close.
 */
export function ApprovalSection({ research }: { research: ResearchRecord }) {
  const me = useCurrentUser();
  const step = useResearchStep(research.id);
  const headingId = useId();
  const roles = rolesFor(research, me);
  const fieldErrors = isApiError(step.error) && step.error.fieldErrors.length > 0;
  const run = (call: StepCall) => {
    step.mutate(call);
  };

  return (
    <Card>
      <section aria-labelledby={headingId} className="flex flex-col gap-4">
        <h2 id={headingId} className="text-base font-semibold text-foreground">
          Approval
        </h2>
        {step.isError && !fieldErrors && <ErrorAlert error={step.error} />}

        {research.status === "draft" && <DraftStep research={research} me={me} roles={roles} step={step} />}

        {research.status === "waiting_approval" && research.request !== null && (
          <div className="flex flex-col gap-4">
            <Alert tone="info">
              <p className="font-medium">
                Waiting for the admin’s approval, for up to {usd(research.request.maxPriceUsd)}.
              </p>
              {research.request.note !== null && <p className="mt-1">“{research.request.note}”</p>}
              <p className="mt-1 text-xs">
                Sent by {roles.isOwner ? "you" : research.owner.name} ·{" "}
                {formatDateTime(research.request.submittedAt)}
              </p>
            </Alert>
            {roles.mayChange && (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => run({ step: "withdraw", body: { version: research.version } })}
                  disabled={step.isPending}
                >
                  Withdraw request
                </Button>
              </div>
            )}
            {roles.mayDecide && <DecisionForms research={research} step={step} />}
          </div>
        )}

        {research.status === "approved" && research.decision !== null && (
          <>
            <Alert tone="success">
              <p className="font-medium">
                Approved up to {usd(research.decision.approvedMaxUsd ?? "0")} by{" "}
                {research.decision.decidedBy.id === me.user.id ? "you" : research.decision.decidedBy.name} ·{" "}
                {formatDateTime(research.decision.decidedAt)}
              </p>
              {research.decision.note !== null && <p className="mt-1">“{research.decision.note}”</p>}
            </Alert>
            {me.permissions.includes("holdings.update") && (
              <div>
                <Link
                  to="/portfolio/new"
                  search={{ research: research.id }}
                  className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                >
                  Bought it? Add to portfolio
                </Link>
              </div>
            )}
          </>
        )}

        {research.status === "rejected" && research.decision !== null && (
          <>
            <Alert tone="warning">
              <p className="font-medium">
                Rejected by{" "}
                {research.decision.decidedBy.id === me.user.id ? "you" : research.decision.decidedBy.name} ·{" "}
                {formatDateTime(research.decision.decidedAt)}
              </p>
              {research.decision.note !== null && <p className="mt-1">“{research.decision.note}”</p>}
            </Alert>
            {roles.mayChange && (
              <div>
                <Button
                  onClick={() => run({ step: "reopen", body: { version: research.version } })}
                  disabled={step.isPending}
                >
                  Revise and send again
                </Button>
              </div>
            )}
          </>
        )}

        {research.status === "closed" && (
          <p className="text-sm text-muted-foreground">
            Closed{research.closedReason === null ? "" : `: “${research.closedReason}”`}. This listing is no
            longer being researched; it can be saved again from Search.
          </p>
        )}

        {research.status !== "closed" &&
          research.status !== "waiting_approval" &&
          (roles.mayChange || roles.mayDecide) && <CloseControl research={research} step={step} />}
      </section>
    </Card>
  );
}

interface DraftStepProps {
  readonly research: ResearchRecord;
  readonly me: MeResponse;
  readonly roles: ReturnType<typeof rolesFor>;
  readonly step: Step;
}

/** What is still needed, and the request form once everything is ready. */
function DraftStep({ research, me, roles, step }: DraftStepProps) {
  const { assessment, price, decision } = research;
  const priceFresh = price !== null && isPriceFresh(price.confirmedAt);
  const ready = assessment !== null && priceFresh;

  return (
    <div className="flex flex-col gap-4">
      {decision?.outcome === "rejected" && (
        <Alert tone="warning">
          <p className="font-medium">
            The last request was rejected by {decision.decidedBy.name} · {formatDateTime(decision.decidedAt)}
          </p>
          {decision.note !== null && <p className="mt-1">“{decision.note}”</p>}
          <p className="mt-1 text-xs">Change what is needed, then send it again.</p>
        </Alert>
      )}
      {roles.isOwner && (
        <p className="text-sm text-muted-foreground">
          {me.user.requiresPurchaseApproval
            ? "The admin has asked you to get approval before buying."
            : "You may buy this card without asking. You can still ask the admin first."}
        </p>
      )}
      <ul aria-label="Ready to send" className="flex flex-col gap-1 text-sm">
        <Readiness done={assessment !== null}>
          {assessment !== null ? "Condition checked" : "Condition not checked yet"}
        </Readiness>
        <Readiness done={priceFresh}>
          {price === null
            ? "Price not checked yet"
            : priceFresh
              ? `Price checked: ${usd(price.totalUsd)} with shipping`
              : `Price checked more than ${String(PRICE_FRESH_HOURS)} hours ago; check it again`}
        </Readiness>
      </ul>
      {!roles.mayChange ? (
        <p className="text-sm text-muted-foreground">
          Only {research.owner.name}, who saved this, can send it for approval.
        </p>
      ) : ready ? (
        // A request sent before (then withdrawn or rejected) is the starting point; otherwise the checked total.
        <SubmitForm
          research={research}
          suggestedMax={research.request?.maxPriceUsd ?? price.totalUsd}
          step={step}
        />
      ) : (
        <p className="text-sm text-muted-foreground">Finish the checks above to send this for approval.</p>
      )}
    </div>
  );
}

function Readiness({ done, children }: { done: boolean; children: string }) {
  return (
    <li
      className={
        done ? "flex items-center gap-2 text-foreground" : "flex items-center gap-2 text-muted-foreground"
      }
    >
      {done ? (
        <Check className="size-4 text-emerald-700 dark:text-emerald-300" aria-label="Done" />
      ) : (
        <X className="size-4 text-amber-700 dark:text-amber-300" aria-label="Not done" />
      )}
      {children}
    </li>
  );
}

function SubmitForm({
  research,
  suggestedMax,
  step,
}: {
  research: ResearchRecord;
  suggestedMax: string;
  step: Step;
}) {
  const [maxPriceUsd, setMaxPriceUsd] = useState(suggestedMax);
  const [note, setNote] = useState(research.request?.note ?? "");
  const [errors, setErrors] = useState<FieldErrors>({});
  const ids = { max: useId(), note: useId() };
  const errorFor = (key: string) => errors[key] ?? serverFieldError(step.error, key);

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(SubmitResearchRequestSchema, { version: research.version, maxPriceUsd, note });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    step.mutate({ step: "submit", body: result.data });
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <Field
        id={ids.max}
        label="Maximum price (US dollars)"
        hint="The most you want to pay, with shipping. Starts at the checked total."
        error={errorFor("maxPriceUsd")}
      >
        <Input
          inputMode="decimal"
          value={maxPriceUsd}
          onChange={(e) => setMaxPriceUsd(e.target.value)}
          className="max-w-xs"
        />
      </Field>
      <Field id={ids.note} label="Why buy this card? (optional)" error={errorFor("note")}>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      </Field>
      <div>
        <Button type="submit" disabled={step.isPending}>
          {step.isPending ? "Sending…" : "Send for approval"}
        </Button>
      </div>
    </form>
  );
}

/** The admin approves (up to the amount asked, or less) or rejects with a reason. */
function DecisionForms({ research, step }: { research: ResearchRecord; step: Step }) {
  const [approvedMaxUsd, setApprovedMaxUsd] = useState(research.request?.maxPriceUsd ?? "");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const ids = { max: useId(), note: useId(), reason: useId() };
  const errorFor = (key: string) => errors[key] ?? serverFieldError(step.error, key);

  function approve(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(ApproveResearchRequestSchema, {
      version: research.version,
      approvedMaxUsd,
      note,
    });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    step.mutate({ step: "approve", body: result.data });
  }

  function reject(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(RejectResearchRequestSchema, { version: research.version, reason });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    step.mutate({ step: "reject", body: result.data });
  }

  return (
    <div className="grid gap-6 border-t border-border pt-4 md:grid-cols-2">
      <form onSubmit={approve} noValidate aria-label="Approve" className="flex flex-col gap-3">
        <Field
          id={ids.max}
          label="Approve up to (US dollars)"
          hint="You may lower it, not raise it."
          error={errorFor("approvedMaxUsd")}
        >
          <Input
            inputMode="decimal"
            value={approvedMaxUsd}
            onChange={(e) => setApprovedMaxUsd(e.target.value)}
          />
        </Field>
        <Field id={ids.note} label="Note (optional)" error={errorFor("note")}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </Field>
        <div>
          <Button type="submit" disabled={step.isPending}>
            Approve
          </Button>
        </div>
      </form>
      <form onSubmit={reject} noValidate aria-label="Reject" className="flex flex-col gap-3">
        <Field id={ids.reason} label="Reason for rejecting" error={errorFor("reason")}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} />
        </Field>
        <div>
          <Button type="submit" variant="danger" disabled={step.isPending}>
            Reject
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Stop researching this listing, with an optional reason. Asks once more before closing. */
function CloseControl({ research, step }: { research: ResearchRecord; step: Step }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const reasonId = useId();

  if (!open) {
    return (
      <div className="border-t border-border pt-4">
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
          Close research…
        </Button>
      </div>
    );
  }

  function close(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(CloseResearchRequestSchema, { version: research.version, reason });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    step.mutate({ step: "close", body: result.data });
  }

  return (
    <form
      onSubmit={close}
      noValidate
      aria-label="Close research"
      className="flex flex-col gap-3 border-t border-border pt-4"
    >
      <p className="text-sm text-muted-foreground">
        Closing stops the research on this listing. The listing can be saved again later from Search.
      </p>
      <Field
        id={reasonId}
        label="Why close it? (optional)"
        error={errors.reason ?? serverFieldError(step.error, "reason")}
      >
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="danger" size="sm" disabled={step.isPending}>
          Close research
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={step.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
